// src/components/Sidebar.tsx
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Box, List, ListItemButton, ListItemIcon, ListItemText,
    IconButton, Typography, Tooltip, Drawer, Avatar, Dialog,
    DialogContent, DialogActions, Button, CircularProgress,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    MenuBook as MenuBookIcon,
    People as PeopleIcon,
    Campaign as CampaignIcon,
    LibraryBooks as SubjectIcon,
    School as SchoolIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Logout as LogoutIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Menu as MenuIcon,
    LocalOffer as PricingIcon,
} from '@mui/icons-material';
import { getAuth } from '../utils/authStorage';

const EXPANDED_WIDTH = 248;
const MINI_WIDTH = 70;

// Light-theme colours
const BG = '#ffffff';
const BG_SECTION = '#f8fafc';
const PRIMARY = '#174143';
const ACCENT = '#3d9ea4';
const ACTIVE_BG = 'linear-gradient(135deg, #174143 0%, #2a6b6f 100%)';
const TEXT_MUTED = '#94a3b8';
const TEXT_NORM = '#374151';
const TEXT_ACT = '#ffffff';

const NAV_SECTIONS = [
    {
        label: 'Content',
        items: [
            { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon sx={{ fontSize: 20 }} /> },
            { label: 'Subjects', to: '/subjects', icon: <SubjectIcon sx={{ fontSize: 20 }} /> },
            { label: 'Classes', to: '/classes', icon: <SchoolIcon sx={{ fontSize: 20 }} /> },
            { label: 'Chapters', to: '/chapters', icon: <MenuBookIcon sx={{ fontSize: 20 }} /> },
            { label: 'Bundle Pricing', to: '/bundle-pricing', icon: <PricingIcon sx={{ fontSize: 20 }} /> },
        ],
    },
    {
        label: 'Engagement',
        items: [
            { label: 'Doubts', to: '/doubts', icon: <QuestionAnswerIcon sx={{ fontSize: 20 }} /> },
            { label: 'Users', to: '/users', icon: <PeopleIcon sx={{ fontSize: 20 }} /> },
            { label: 'Notifications', to: '/notifications', icon: <CampaignIcon sx={{ fontSize: 20 }} /> },
        ],
    },
];

interface SidebarContentProps {
    collapsed: boolean;
    onToggle: () => void;
    onClose?: () => void;
}

function SidebarContent({ collapsed, onToggle, onClose }: SidebarContentProps) {
    const location = useLocation();
    const auth = getAuth();
    const user = auth?.user;
    const displayName: string = user?.displayName || user?.firstName || 'Admin';
    const email: string = user?.email || '';
    const initials = displayName.split(' ').map((p: string) => p[0]).join('').toUpperCase().slice(0, 2);

    const isActive = (to: string) =>
        location.pathname === to || location.pathname.startsWith(to + '/');

    const [logoutOpen, setLogoutOpen] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    const handleLogout = () => {
        setLogoutLoading(true);
        sessionStorage.clear();
        localStorage.removeItem('auth_token');
        setTimeout(() => {
            window.location.href = '/login';
        }, 600);
    };

    return (
        <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column',
            bgcolor: BG, overflow: 'hidden',
            transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
            width: collapsed ? MINI_WIDTH : EXPANDED_WIDTH,
            borderRight: '1px solid #e5e7eb',
            boxShadow: '2px 0 12px rgba(0,0,0,0.05)',
        }}>

            {/* ── Brand header ── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', minHeight: 64,
                px: collapsed ? 1.5 : 2.5,
                justifyContent: collapsed ? 'center' : 'space-between',
                borderBottom: '1px solid #f1f5f9',
                flexShrink: 0,
            }}>
                {!collapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 1.5,
                            background: ACTIVE_BG, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(23,65,67,0.3)',
                        }}>
                            <SchoolIcon sx={{ fontSize: 17, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography sx={{
                                fontWeight: 800, fontSize: '0.88rem', lineHeight: 1.1, letterSpacing: '-0.01em',
                                background: `linear-gradient(135deg, ${PRIMARY} 0%, ${ACCENT} 100%)`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                            }}>
                                Vidya Samruddhi
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: TEXT_MUTED, letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>
                                Admin Panel
                            </Typography>
                        </Box>
                    </Box>
                )}

                {collapsed && (
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 1.5, background: ACTIVE_BG,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 10px rgba(23,65,67,0.3)',
                    }}>
                        <SchoolIcon sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>
                )}

                {!collapsed && (
                    <Tooltip title="Collapse sidebar" placement="right">
                        <IconButton size="small" onClick={() => { onToggle(); onClose?.(); }}
                            sx={{ color: TEXT_MUTED, '&:hover': { color: PRIMARY, background: '#f1f5f9' }, borderRadius: 1.5 }}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* ── Navigation ── */}
            <Box sx={{
                flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2, px: collapsed ? 1 : 1.5,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 2 },
            }}>
                {NAV_SECTIONS.map((section, si) => (
                    <Box key={si} sx={{ mb: collapsed ? 2 : 1 }}>
                        {!collapsed && (
                            <Typography sx={{
                                fontSize: '0.62rem', fontWeight: 800, letterSpacing: 1.2,
                                textTransform: 'uppercase', color: TEXT_MUTED,
                                px: 1.5, mb: 0.75, mt: si > 0 ? 2 : 0,
                            }}>
                                {section.label}
                            </Typography>
                        )}
                        {si > 0 && collapsed && (
                            <Box sx={{ height: 1, background: '#f1f5f9', mx: 1, mb: 1.5 }} />
                        )}

                        <List dense disablePadding>
                            {section.items.map(({ label, to, icon }) => {
                                const active = isActive(to);
                                return (
                                    <Tooltip key={to} title={collapsed ? label : ''} placement="right" arrow>
                                        <ListItemButton
                                            component={NavLink}
                                            to={to}
                                            onClick={onClose}
                                            sx={{
                                                borderRadius: 2, mb: 0.5,
                                                px: collapsed ? 0 : 1.5, py: 0.9,
                                                justifyContent: collapsed ? 'center' : 'flex-start',
                                                position: 'relative', overflow: 'hidden',
                                                background: active ? ACTIVE_BG : 'transparent',
                                                boxShadow: active ? '0 4px 12px rgba(23,65,67,0.2)' : 'none',
                                                '&:hover': { background: active ? ACTIVE_BG : '#f0fafa' },
                                                '&::before': active && !collapsed ? {
                                                    content: '""', position: 'absolute',
                                                    left: 0, top: '20%', height: '60%',
                                                    width: 3, borderRadius: '0 2px 2px 0',
                                                    background: ACCENT,
                                                } : {},
                                                '& .MuiListItemIcon-root': {
                                                    color: active ? TEXT_ACT : ACCENT,
                                                    minWidth: collapsed ? 0 : 32,
                                                    transition: 'color 0.15s',
                                                },
                                                transition: 'all 0.15s ease',
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32 }}>
                                                {icon}
                                            </ListItemIcon>
                                            {!collapsed && (
                                                <ListItemText
                                                    primary={label}
                                                    primaryTypographyProps={{
                                                        fontWeight: active ? 700 : 500,
                                                        fontSize: '0.84rem',
                                                        color: active ? TEXT_ACT : TEXT_NORM,
                                                    }}
                                                />
                                            )}
                                        </ListItemButton>
                                    </Tooltip>
                                );
                            })}
                        </List>
                    </Box>
                ))}
            </Box>

            {/* ── Footer: user card + logout ── */}
            <Box sx={{ borderTop: '1px solid #f1f5f9', p: collapsed ? 1 : 1.5, flexShrink: 0 }}>
                {!collapsed ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        p: 1.25, borderRadius: 2,
                        background: BG_SECTION, border: '1px solid #e5e7eb', mb: 1,
                    }}>
                        <Avatar sx={{
                            width: 34, height: 34, flexShrink: 0,
                            background: ACTIVE_BG, fontSize: '0.78rem', fontWeight: 800,
                            boxShadow: '0 2px 8px rgba(23,65,67,0.25)',
                        }}>
                            {initials}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden', flex: 1 }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: PRIMARY, lineHeight: 1.2 }} noWrap>
                                {displayName}
                            </Typography>
                            {email && (
                                <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, display: 'block' }} noWrap>
                                    {email}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Tooltip title={displayName} placement="right">
                        <Avatar sx={{
                            width: 36, height: 36, mx: 'auto', mb: 1,
                            background: ACTIVE_BG, fontSize: '0.78rem', fontWeight: 800,
                            boxShadow: '0 2px 8px rgba(23,65,67,0.25)', cursor: 'pointer',
                        }}>
                            {initials}
                        </Avatar>
                    </Tooltip>
                )}

                {/* Logout */}
                <Tooltip title="Logout" placement="right">
                    <ListItemButton onClick={() => setLogoutOpen(true)} sx={{
                        borderRadius: 2, py: 0.9,
                        px: collapsed ? 0 : 1.5,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        '&:hover': { background: '#fef2f2' },
                        '& .MuiListItemIcon-root': { color: '#ef4444', minWidth: collapsed ? 0 : 32 },
                    }}>
                        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32 }}>
                            <LogoutIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                        </ListItemIcon>
                        {!collapsed && (
                            <ListItemText
                                primary="Logout"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.84rem', color: '#ef4444' }}
                            />
                        )}
                    </ListItemButton>
                </Tooltip>

                {/* Expand button (only when collapsed) */}
                {collapsed && (
                    <Tooltip title="Expand sidebar" placement="right">
                        <IconButton size="small" onClick={onToggle}
                            sx={{ display: 'flex', mx: 'auto', mt: 0.5, color: TEXT_MUTED, '&:hover': { color: PRIMARY, background: '#f1f5f9' } }}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* ══ Logout Confirmation Dialog ══ */}
            <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)', px: 3, py: 2.5, color: '#fff' }}>
                    <Typography variant="h6" fontWeight={800}>Logout Options</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>Are you sure you want to end your session?</Typography>
                </Box>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        You will need to log in again to access the admin panel.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setLogoutOpen(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleLogout}
                        disabled={logoutLoading}
                        sx={{ background: '#dc2626', fontWeight: 700, px: 3, borderRadius: 2, '&:hover': { background: '#b91c1c' } }}
                    >
                        {logoutLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Logout Now'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps): React.ReactElement {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <>
            {/* Mobile hamburger */}
            <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                    display: { md: 'none' },
                    position: 'fixed', top: 12, left: 12,
                    zIndex: t => t.zIndex.drawer + 5,
                    bgcolor: '#fff', color: PRIMARY,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: '#f1f5f9' },
                }}
                aria-label="Open menu"
            >
                <MenuIcon />
            </IconButton>

            {/* Desktop permanent sidebar */}
            <Box component="nav" sx={{
                display: { xs: 'none', md: 'flex' }, flexShrink: 0,
                width: collapsed ? MINI_WIDTH : EXPANDED_WIDTH,
                transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
                position: 'fixed', top: 0, left: 0, height: '100vh',
                zIndex: t => t.zIndex.drawer,
            }}>
                <SidebarContent collapsed={collapsed} onToggle={onToggle} />
            </Box>

            {/* Mobile temporary drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { border: 'none', bgcolor: '#fff' } }}
            >
                <SidebarContent collapsed={false} onToggle={() => { }} onClose={() => setMobileOpen(false)} />
            </Drawer>
        </>
    );
}

export { EXPANDED_WIDTH, MINI_WIDTH };
